import React from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { formatMoney } from "./quotePricingMath";
import { customerQuotePresentationFromComponents } from "./wixJobQuote";

export default function CustomerQuotePackagesView({
    components,
    currency = "USD",
    title = "Customer quote",
    caption = "Package totals only. Included work is listed without hours, rates, or PBR buy costs. Customer-purchased equipment is itemized.",
    variant = "outlined",
    sx,
}) {
    const presentation = customerQuotePresentationFromComponents(components);
    const { packages, customerItems, parentTitles } = presentation;
    if (!packages.length && !customerItems.length) return null;

    return (
        <Paper variant={variant} sx={{ overflow: "hidden", ...sx }}>
            <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: "divider" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    {title}
                </Typography>
                {caption ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {caption}
                    </Typography>
                ) : null}
                {parentTitles.length ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                        Project scope: {parentTitles.join(", ")}
                    </Typography>
                ) : null}
            </Box>
            <Stack spacing={1.75} sx={{ p: 1.5 }}>
                {packages.map((pkg) => (
                    <Box key={pkg.id || pkg.title}>
                        <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="baseline">
                            <Typography variant="body1" sx={{ fontWeight: 800, lineHeight: 1.3 }}>
                                {pkg.title}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>
                                {formatMoney(pkg.amount, currency)}
                            </Typography>
                        </Stack>
                        {pkg.included.length ? (
                            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.25 }}>
                                {pkg.included.map((item, index) => (
                                    <Typography key={`${pkg.id || pkg.title}-${index}`} component="li" variant="body2" color="text.secondary" sx={{ pl: 0.25 }}>
                                        {item}
                                    </Typography>
                                ))}
                            </Box>
                        ) : null}
                    </Box>
                ))}
                {customerItems.length ? (
                    <Box sx={{ pt: 0.5, borderTop: 1, borderColor: "divider" }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>
                            Customer-supplied equipment
                        </Typography>
                        <Stack spacing={0.5}>
                            {customerItems.map((item) => (
                                <Stack
                                    key={item.id || item.title}
                                    direction="row"
                                    spacing={1.5}
                                    justifyContent="space-between"
                                    alignItems="baseline"
                                >
                                    <Typography variant="body2">{item.title}</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                                        {formatMoney(item.amount, currency)}
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>
                    </Box>
                ) : null}
            </Stack>
        </Paper>
    );
}
